/**
 * DispatchPanel — governed coding-agent dispatch with a reviewable,
 * reversible file-patch diff.
 *
 * Thin UI over the pure dispatch engine (src/lib/dev/dispatch.ts):
 *   • Blast-radius sliders (max files / max diff lines) + allowed-path
 *     input seal into a DispatchContract BEFORE the agent runs.
 *   • Dispatch calls the REAL argoWorker when configured
 *     (src/lib/agent/argoWorker.ts) and the deterministic fallback worker
 *     when not — same seam, one-flag swap, honestly labeled either way.
 *   • Scope is enforced before any diff exists: an escaping patch renders
 *     its violations, never a reviewable artifact.
 *   • The actionDiff renders per-file hunks (removed/added lines) for
 *     Approve / Reject. Approve persists a 'dispatched' DevDispatch record
 *     carrying the sealed-ready apply/revert bundle summary; Reject
 *     discards everything. The revert half of the bundle is the exact
 *     inverse patch, so an approved dispatch stays reversible.
 *
 * All decisions live in the pure modules; this component only renders
 * state and forwards clicks.
 */

import { useMemo, useState } from 'react';
import { Check, FileDiff, Loader2, Send, ShieldAlert, Undo2, X } from 'lucide-react';
import { GlassPanel } from '../GlassPanel';
import { C } from '../../lib/tokens';
import { buildContract, type BlastScope } from '../../lib/dev/dispatchContract';
import {
  dispatchTicket,
  makeArgoWorker,
  makeFallbackWorker,
  type DispatchResult,
} from '../../lib/dev/dispatch';
import { isConfigured, runAgent } from '../../lib/agent/argoWorker';
import { saveDevDispatch } from '../../lib/db/devDispatch';

export interface DispatchPanelProps {
  /** The dev ticket being handed to the coding agent. */
  ticket: { id: string; title: string };
  /** Target repo label, denormalised onto the persisted dispatch. */
  repo?: string;
}

type Phase = 'idle' | 'running' | 'reviewed' | 'approved';

export function DispatchPanel({ ticket, repo = 'argo/argo-os' }: DispatchPanelProps) {
  const [maxFiles, setMaxFiles] = useState(3);
  const [maxDiffLines, setMaxDiffLines] = useState(120);
  const [pathsText, setPathsText] = useState('src/lib/dev');
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<DispatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const live = isConfigured();

  const scope: BlastScope = useMemo(
    () => ({
      repos: [repo],
      paths: pathsText
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
      maxFiles,
      maxDiffLines,
    }),
    [repo, pathsText, maxFiles, maxDiffLines],
  );

  const dispatch = async () => {
    setPhase('running');
    setError(null);
    setResult(null);
    try {
      const contract = buildContract({ id: ticket.id, title: ticket.title }, scope);
      const worker = live ? makeArgoWorker({ runAgent }) : makeFallbackWorker();
      const res = await dispatchTicket(worker, contract);
      setResult(res);
      setPhase('reviewed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dispatch failed.');
      setPhase('idle');
    }
  };

  const approve = async () => {
    if (!result?.ok || !result.bundle) return;
    setError(null);
    try {
      await saveDevDispatch({
        ticketId: ticket.id,
        ticketTitle: ticket.title,
        repo,
        state: 'dispatched',
      });
      setPhase('approved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not persist the dispatch.');
    }
  };

  const reject = () => {
    setResult(null);
    setPhase('idle');
  };

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px', display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div className="t-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Send size={14} aria-hidden="true" />
          Dispatch coding agent
        </div>
        <span className="glass-pill" title={live ? 'Live Argo agent-worker' : 'No worker configured — deterministic fallback'}>
          {live ? 'live worker' : 'fallback'}
        </span>
      </div>

      <div className="t-meta">
        {ticket.title} <span style={{ color: C.textDim }}>· {ticket.id} · {repo}</span>
      </div>

      {/* Blast-radius controls — sealed into the contract before dispatch. */}
      <div style={{ display: 'grid', gap: 8 }}>
        <label className="t-meta" style={{ display: 'grid', gap: 4 }}>
          Allowed paths (comma-separated)
          <input
            type="text"
            value={pathsText}
            onChange={(e) => setPathsText(e.target.value)}
            disabled={phase === 'running'}
            style={{
              background: C.ink800,
              border: '1px solid rgba(255,255,255,0.09)',
              borderRadius: 10,
              color: C.text,
              padding: '6px 10px',
              font: 'inherit',
            }}
          />
        </label>
        <label className="t-meta" style={{ display: 'grid', gap: 4 }}>
          Max files: {maxFiles}
          <input
            type="range"
            min={1}
            max={20}
            value={maxFiles}
            disabled={phase === 'running'}
            onChange={(e) => setMaxFiles(Number(e.target.value))}
            aria-label="Max files"
          />
        </label>
        <label className="t-meta" style={{ display: 'grid', gap: 4 }}>
          Max diff lines: {maxDiffLines}
          <input
            type="range"
            min={10}
            max={500}
            step={10}
            value={maxDiffLines}
            disabled={phase === 'running'}
            onChange={(e) => setMaxDiffLines(Number(e.target.value))}
            aria-label="Max diff lines"
          />
        </label>
      </div>

      {phase !== 'reviewed' && phase !== 'approved' && (
        <button
          type="button"
          className="glass-btn glass-btn--primary"
          onClick={dispatch}
          disabled={phase === 'running' || scope.paths.length === 0}
          style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          {phase === 'running' ? (
            <>
              <Loader2 size={14} style={{ animation: 'auth-spin 0.8s linear infinite' }} aria-hidden="true" /> Dispatching…
            </>
          ) : (
            <>
              <Send size={14} aria-hidden="true" /> Dispatch
            </>
          )}
        </button>
      )}

      {error && (
        <div role="alert" className="t-meta" style={{ color: C.orangeWarm }}>
          {error}
        </div>
      )}

      {/* Scope violations: the honest rejection — no diff ever existed. */}
      {result && !result.ok && (
        <div role="alert" style={{ display: 'grid', gap: 6 }}>
          <div className="t-meta" style={{ color: C.orangeWarm, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShieldAlert size={13} aria-hidden="true" />
            Rejected before review — patch escaped its blast radius
          </div>
          {result.violations.map((v) => (
            <div key={v} className="t-meta" style={{ color: C.orangeWarm, paddingLeft: 19 }}>
              {v}
            </div>
          ))}
          <button type="button" className="glass-btn" onClick={reject} style={{ justifySelf: 'start' }}>
            Adjust scope & retry
          </button>
        </div>
      )}

      {/* The reviewable actionDiff: one hunk per file. */}
      {result?.ok && result.diff && (
        <div style={{ display: 'grid', gap: 10 }}>
          <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileDiff size={13} aria-hidden="true" />
            {result.summary || 'Proposed patch'} · {result.diff.totalFiles} file
            {result.diff.totalFiles === 1 ? '' : 's'}, {result.diff.totalChangedLines} changed lines
          </div>

          {result.diff.files.map((f) => (
            <div
              key={f.path}
              style={{
                border: '1px solid rgba(255,255,255,0.09)',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              <div className="t-meta" style={{ padding: '6px 10px', background: C.ink800 }}>
                {f.path} <span style={{ color: C.textDim }}>@ line {f.hunk.startLine}</span>
              </div>
              <pre style={{ margin: 0, padding: '8px 10px', fontSize: 12, overflowX: 'auto' }}>
                {f.hunk.removed.map((line, i) => (
                  <div key={`r${i}`} style={{ color: C.orangeWarm }}>
                    - {line}
                  </div>
                ))}
                {f.hunk.added.map((line, i) => (
                  <div key={`a${i}`} style={{ color: C.green }}>
                    + {line}
                  </div>
                ))}
              </pre>
            </div>
          ))}

          <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.textDim }}>
            <Undo2 size={12} aria-hidden="true" />
            Reversible: the sealed bundle carries the exact inverse patch.
          </div>

          {phase === 'approved' ? (
            <div className="t-meta" style={{ color: C.green, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Check size={13} aria-hidden="true" /> Approved — dispatch recorded.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                onClick={approve}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Check size={14} aria-hidden="true" /> Approve
              </button>
              <button
                type="button"
                className="glass-btn"
                onClick={reject}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <X size={14} aria-hidden="true" /> Reject
              </button>
            </div>
          )}
        </div>
      )}
    </GlassPanel>
  );
}

export default DispatchPanel;
